import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../config/firebase';
import { fetchWithRetries } from '../context/AuthContext';
import './FormPage.css';

type VerifyStep = 'idle' | 'sending' | 'awaiting_code' | 'verifying';
type FitbitStep = 'idle' | 'connecting' | 'disconnecting';

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length <= 3) return local.length ? `(${local}` : '';
  if (local.length <= 6) return `(${local.slice(0, 3)})-${local.slice(3)}`;
  return `(${local.slice(0, 3)})-${local.slice(3, 6)}-${local.slice(6, 10)}`;
}

function unformatPhone(formatted: string): string {
  return formatted.replace(/\D/g, '').slice(0, 10);
}

function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [settings, setSettings] = useState({
    name: '',
    phoneNumber: '',
    timezone: 'America/New_York',
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [verifyStep, setVerifyStep] = useState<VerifyStep>('idle');
  const [verifyCode, setVerifyCode] = useState('');
  const [phoneLast4, setPhoneLast4] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('');
  const pendingUpdates = useRef<Record<string, string> | null>(null);

  const [fitbitConnected, setFitbitConnected] = useState(false);
  const [fitbitStep, setFitbitStep] = useState<FitbitStep>('idle');
  const [fitbitMsg, setFitbitMsg] = useState('');

  // Pre-populate from user profile
  useEffect(() => {
    if (user) {
      setSettings((s) => ({
        ...s,
        name: user.name || '',
        phoneNumber: user.phoneNumber || '',
      }));
    }
  }, [user]);

  // Fetch timezone + subscription status + Fitbit status
  useEffect(() => {
    (async () => {
      try {
        const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
        const idToken = await auth?.currentUser?.getIdToken(true);
        if (!idToken) return;

        const [meRes, subRes, fitbitRes] = await Promise.all([
          fetchWithRetries(`${API_BASE}/auth/me`, {
            headers: { Authorization: `Bearer ${idToken}` },
          }),
          fetchWithRetries(`${API_BASE}/subscription/status`, {
            headers: { Authorization: `Bearer ${idToken}` },
          }),
          fetchWithRetries(`${API_BASE}/fitbit/status`, {
            headers: { Authorization: `Bearer ${idToken}` },
          }),
        ]);

        if (meRes && meRes.ok) {
          const json = await meRes.json();
          if (json?.data?.timezone) {
            setSettings((s) => ({ ...s, timezone: json.data.timezone }));
          }
        }

        if (subRes && subRes.ok) {
          const json = await subRes.json();
          if (json?.data?.subscriptionStatus) {
            setSubscriptionStatus(json.data.subscriptionStatus);
          }
        }

        if (fitbitRes && fitbitRes.ok) {
          const json = await fitbitRes.json();
          setFitbitConnected(!!(json?.data?.connected));
        }
      } catch {
        /* non-critical */
      }
    })();
  }, []);

  // Handle ?fitbit= query param set by the OAuth callback redirect
  useEffect(() => {
    const fitbitParam = searchParams.get('fitbit');
    if (fitbitParam === 'connected') {
      setFitbitConnected(true);
      setFitbitMsg('Fitbit connected successfully!');
      setSearchParams((p) => { p.delete('fitbit'); return p; }, { replace: true });
    } else if (fitbitParam === 'error') {
      setFitbitMsg('Fitbit connection failed. Please try again.');
      setSearchParams((p) => { p.delete('fitbit'); return p; }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMsg('');

    const rawPhone = settings.phoneNumber.replace(/\D/g, '');
    const updates: Record<string, string> = {
      name: settings.name,
      phoneNumber: rawPhone.length === 10 ? `+1${rawPhone}` : settings.phoneNumber,
      timezone: settings.timezone,
    };
    pendingUpdates.current = updates;

    try {
      setVerifyStep('sending');
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      const idToken = await auth?.currentUser?.getIdToken(true);
      if (!idToken) throw new Error('Not authenticated');

      const res = await fetchWithRetries(`${API_BASE}/users/request-settings-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!res || !res.ok) {
        setSaveMsg('Failed to send verification code. Please try again.');
        setVerifyStep('idle');
        return;
      }

      const data = await res.json().catch(() => null);
      setPhoneLast4(data?.phoneLast4 || '****');
      setVerifyStep('awaiting_code');
      setVerifyCode('');
    } catch (err: any) {
      setSaveMsg(err?.message || 'Failed to send verification code. Please try again.');
      setVerifyStep('idle');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyAndUpdate = async () => {
    if (!verifyCode.trim() || verifyCode.length !== 6) {
      setSaveMsg('Please enter the 6-digit code.');
      return;
    }
    setVerifyStep('verifying');
    setSaveMsg('');

    try {
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      const idToken = await auth?.currentUser?.getIdToken(true);
      if (!idToken) throw new Error('Not authenticated');

      const res = await fetchWithRetries(`${API_BASE}/users/verify-and-update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          code: verifyCode.trim(),
          updates: pendingUpdates.current,
        }),
      });

      if (!res || !res.ok) {
        setSaveMsg('Verification failed. Please try again.');
        setVerifyStep('awaiting_code');
        return;
      }

      setSaveMsg('Settings updated successfully!');
      setVerifyStep('idle');
      setVerifyCode('');
      pendingUpdates.current = null;
    } catch (err: any) {
      setSaveMsg(err?.message || 'Verification failed. Please try again.');
      setVerifyStep('awaiting_code');
    }
  };

  const handleCancelVerify = () => {
    setVerifyStep('idle');
    setVerifyCode('');
    pendingUpdates.current = null;
    setSaveMsg('');
  };

  const handleConnectFitbit = async () => {
    setFitbitStep('connecting');
    setFitbitMsg('');
    try {
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      const idToken = await auth?.currentUser?.getIdToken(true);
      if (!idToken) throw new Error('Not authenticated');

      const res = await fetchWithRetries(`${API_BASE}/fitbit/init`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (!res || !res.ok) {
        setFitbitMsg('Failed to start Fitbit connection. Please try again.');
        setFitbitStep('idle');
        return;
      }

      const json = await res.json();
      const authUrl = json?.data?.authUrl;
      if (!authUrl) {
        setFitbitMsg('Failed to get Fitbit authorization URL.');
        setFitbitStep('idle');
        return;
      }

      // Redirect the browser to Fitbit's OAuth page
      window.location.href = authUrl;
    } catch {
      setFitbitMsg('Failed to start Fitbit connection. Please try again.');
      setFitbitStep('idle');
    }
  };

  const handleDisconnectFitbit = async () => {
    setFitbitStep('disconnecting');
    setFitbitMsg('');
    try {
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      const idToken = await auth?.currentUser?.getIdToken(true);
      if (!idToken) throw new Error('Not authenticated');

      const res = await fetchWithRetries(`${API_BASE}/fitbit/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (!res || !res.ok) {
        setFitbitMsg('Failed to disconnect Fitbit. Please try again.');
        setFitbitStep('idle');
        return;
      }

      setFitbitConnected(false);
      setFitbitMsg('Fitbit disconnected.');
    } catch {
      setFitbitMsg('Failed to disconnect Fitbit. Please try again.');
    } finally {
      setFitbitStep('idle');
    }
  };

  const handleManageBilling = () => {
    navigate('/billing');
  };

  const handleReactivate = () => {
    navigate('/checkout?mode=reactivate');
  };

  return (
    <div className="form-page">
      <header className="page-header">
        <h1>Settings</h1>
        <p>Manage your account and preferences</p>
      </header>

      <form onSubmit={handleSave}>
        <section className="settings-section card">
          <h2>Profile</h2>

          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              value={settings.name}
              onChange={(e) => setSettings({ ...settings, name: e.target.value })}
              placeholder="Your name"
              disabled={verifyStep !== 'idle'}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <input
              type="tel"
              id="phoneNumber"
              value={formatPhone(settings.phoneNumber)}
              onChange={(e) => setSettings({ ...settings, phoneNumber: unformatPhone(e.target.value) })}
              placeholder="(555)-000-0000"
              disabled={verifyStep !== 'idle'}
            />
            <span className="help-text">Used for your SMS wellness assistant</span>
          </div>

          <div className="form-group">
            <label htmlFor="timezone">Timezone</label>
            <select
              id="timezone"
              value={settings.timezone}
              onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
              disabled={verifyStep !== 'idle'}
            >
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
            </select>
          </div>
        </section>

        {verifyStep === 'awaiting_code' && (
          <section className="settings-section card" style={{ borderLeft: '3px solid var(--primary)' }}>
            <h2>Verify Your Identity</h2>
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '16px' }}>
              We sent a 6-digit code to your phone ending in <strong>...{phoneLast4}</strong>.
            </p>
            <div className="form-group">
              <label htmlFor="verifyCode">Verification Code</label>
              <input
                type="text"
                id="verifyCode"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                autoFocus
                style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.25rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={handleVerifyAndUpdate}
                disabled={verifyCode.length !== 6}
              >
                Confirm
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={handleCancelVerify}
              >
                Cancel
              </button>
            </div>
          </section>
        )}

        {verifyStep === 'verifying' && (
          <p style={{ textAlign: 'center', color: '#555', margin: '16px 0' }}>Verifying...</p>
        )}

        {saveMsg && (
          <p
            style={{
              textAlign: 'center',
              marginBottom: '12px',
              color: saveMsg.includes('success') ? 'var(--primary)' : '#d32f2f',
            }}
          >
            {saveMsg}
          </p>
        )}

        {verifyStep === 'idle' && (
          <button type="submit" className="btn btn-primary btn-full mt-lg" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        )}
      </form>

      <section className="settings-section card" style={{ marginTop: '24px' }}>
        <h2>Subscription &amp; Billing</h2>
        {subscriptionStatus === 'active' ? (
          <>
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '16px' }}>
              Your subscription is <strong style={{ color: 'var(--primary)' }}>active</strong>.
            </p>
            <button type="button" className="btn btn-secondary btn-full" onClick={handleManageBilling}>
              Manage Billing
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '16px' }}>
              Your subscription is <strong style={{ color: '#d32f2f' }}>inactive</strong>.
            </p>
            <button type="button" className="btn btn-primary btn-full" onClick={handleReactivate}>
              Reactivate Subscription
            </button>
          </>
        )}
      </section>

      <section className="settings-section card" style={{ marginTop: '24px' }}>
        <h2>Fitbit</h2>
        {fitbitConnected ? (
          <>
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '16px' }}>
              Your Fitbit account is <strong style={{ color: 'var(--primary)' }}>connected</strong>.
              Activity, sleep, and heart rate data will be used to enhance your wellness insights.
            </p>
            <button
              type="button"
              className="btn btn-secondary btn-full"
              onClick={handleDisconnectFitbit}
              disabled={fitbitStep !== 'idle'}
            >
              {fitbitStep === 'disconnecting' ? 'Disconnecting...' : 'Disconnect Fitbit'}
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '16px' }}>
              Connect your Fitbit to automatically sync activity, sleep, and heart rate data.
            </p>
            <button
              type="button"
              className="btn btn-primary btn-full"
              onClick={handleConnectFitbit}
              disabled={fitbitStep !== 'idle'}
            >
              {fitbitStep === 'connecting' ? 'Connecting...' : 'Connect Fitbit'}
            </button>
          </>
        )}
        {fitbitMsg && (
          <p
            style={{
              marginTop: '12px',
              fontSize: '0.9rem',
              textAlign: 'center',
              color: fitbitMsg.includes('success') || fitbitMsg.includes('connected')
                ? 'var(--primary)'
                : '#d32f2f',
            }}
          >
            {fitbitMsg}
          </p>
        )}
      </section>
    </div>
  );
}

export default Settings;